import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import _ from 'lodash';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { AccordionModule } from 'primeng/accordion';
import { CardModule } from 'primeng/card';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { diff_match_patch, DIFF_DELETE, DIFF_INSERT, DIFF_EQUAL } from '../../../@core/utils/diff-match-patch';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CONTRACT_CHECKLIST_STATUS } from '../../../@core/models/enums';
import { CONTRACT_USER_ROLE } from '../../../@core/models/model';
import { SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';
import { ToastService } from '../../../@core/services/toast.service';
import { toFixedNumber } from '../../../common/utils';
import { IChecklistItem } from '../../models/settings.model';
import { ChecklistsService } from '../../services/checklists.service';
import { ContractChecklistService, IGetContractChecklist } from '../../services/contract-checklist-item.service';
import { ContractInsightsService } from '../../services/contract-insights.service';
import { ContractService } from '../../services/contracts.service';
import { GAUGE_COLORS, setGuageChart } from '../../utils';
import { RiskMatrixComponent } from "../risk-matrix/risk-matrix.component";
import { AssetUrlPipe } from '../../../@shared/pipes/asset-url.pipe';
import { CONTRACT_MANAGEMENT_SVG } from 'src/app/common/svgs';
import { CreditsOverviewComponent } from 'src/app/features/credits/components/credits-overview/credits-overview.component';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';

const IMPACT_COLOR = {
  1: '#1EB768',
  2: '#B2D34A',
  3: '#F7A232',
  4: '#E86829',
  5: '#DC3622'
}

@Component({
  selector: 'app-check-points',
  imports: [CommonModule, ButtonComponent, NgxEchartsModule, TooltipModule, AccordionModule, CardModule, DialogComponent, RadioButtonModule, FormsModule, RiskMatrixComponent, ProgressSpinnerModule, TitleCasePipe, AssetUrlPipe, CreditsOverviewComponent],
  providers: [
    {
      provide: NGX_ECHARTS_CONFIG,
      useValue: {
        echarts: () => import('echarts')
      }
    }
  ],
  standalone: true,
  templateUrl: './check-points.component.html',
  styleUrl: './check-points.component.scss'
})
export class CheckPointsComponent implements OnInit, OnDestroy {

  @Input() contractId: string = "";
  @Input() role: string = "";
  checklists: IGetContractChecklist[] = []
  contractChecklistStatus = CONTRACT_CHECKLIST_STATUS
  statusCount: Record<CONTRACT_CHECKLIST_STATUS, number> = {
    [CONTRACT_CHECKLIST_STATUS.COMPLIANCE]: 0,
    [CONTRACT_CHECKLIST_STATUS.NOT_IN_COMPLIANCE]: 0,
    [CONTRACT_CHECKLIST_STATUS.SAVE_FOR_LATER]: 0
  }
  IMPACT_COLOR: { [key: number]: string } = IMPACT_COLOR;
  complianceRate: number = 0
  chartOption: echarts.EChartsOption = {}
  loading: boolean = false
  aiLoading: boolean = true
  selectedChecklist: IChecklistItem['_id'] = '';
  sourceDialog: {
    selectedChecklist: any,
    currentIdx: number
    visible: boolean,
    selectedSource: string,
    selectedRecommended: string
    selectedSourceDocument: string
  } = { visible: false, selectedSource: '', selectedRecommended: '', selectedSourceDocument: '', selectedChecklist: '', currentIdx: 0 };

  rulesDialog: {
    visible: boolean,
    checklistId: string | null,
    rules: { rule: string, impact: number }[],
    selectedRule: { rule: string, impact: number },
  } = { visible: false, checklistId: null, rules: [], selectedRule: { rule: '', impact: 0 } };

  riskMatrixDialog: {
    visible: boolean,
    selectedImpact: number,
    selectedProbability: number
  } = { visible: false, selectedImpact: 0, selectedProbability: 0 };
  extractChecklistsSubscription: any
  riskScore: number = 0
  riskChartOption: echarts.EChartsOption = {}
  checklistItemsInSync: boolean = false
  /** True while a stop request is in-flight, to prevent double-clicks. */
  stoppingExtraction: boolean = false

  CONTRACT_USER_ROLE = CONTRACT_USER_ROLE;
  CONTRACT_MANAGEMENT_SVG = CONTRACT_MANAGEMENT_SVG;
  exhaustionState: 'depleted' | 'ready' | null = null;
  constructor(private contractChecklistService: ContractChecklistService, private toastService: ToastService, private checklistService: ChecklistsService, private contractInsightsService: ContractInsightsService, private socketService: SocketService, private contractService: ContractService, private sanitizer: DomSanitizer, private userCreditsState: UserCreditsStateService) {
    this.extractChecklistsSubscription = this.socketService.on<{ checklists: IGetContractChecklist[], lastElement?: boolean, singleElement: boolean }>(SOCKET_EVENTS.CHECKLIST_ITEMS_EXTRACTED).subscribe(result => {
      this.updateChecklistsData(result[0].checklists, result[0].lastElement, result[0].singleElement);
    });
  }

  ngOnInit(): void {
    this.complianceRate = 0
    this.chartOption = setGuageChart(this.complianceRate, [GAUGE_COLORS.RED, GAUGE_COLORS.YELLOW, GAUGE_COLORS.GREEN])
    this.riskChartOption = setGuageChart(this.riskScore, [GAUGE_COLORS.GREEN, GAUGE_COLORS.YELLOW, GAUGE_COLORS.RED])
    this.getContractChecklistMappings();
  }

  ngOnDestroy() {
    if (this.extractChecklistsSubscription) {
      this.extractChecklistsSubscription.unsubscribe();
    }
  }

  /** Stop a running AI checklist extraction for this contract. */
  stopExtracting() {
    if (this.stoppingExtraction) return
    this.stoppingExtraction = true
    this.contractChecklistService.stopChecklistExtraction(this.contractId).subscribe({
      next: res => {
        this.stoppingExtraction = false
        if (res.success) {
          this.aiLoading = false
          this.toastService.success('Checklist extraction stopped.')
        }
      },
      error: () => {
        this.stoppingExtraction = false
        this.toastService.error('Could not stop the extraction. Please try again.')
      }
    })
  }

  private isAgentCreditsExhausted(): boolean {
    const remaining = this.userCreditsState.credits()?.ask?.remaining;
    return remaining !== undefined && remaining <= 0;
  }

  extractChecklists(refresh: boolean = false, checklistId?: IGetContractChecklist['_id']) {
    if (this.isAgentCreditsExhausted()) {
      this.toastService.error('Ask credits exhausted. Please request more to continue.');
      return;
    }
    if (refresh) {
      this.checklists = []
      this.chartOption = setGuageChart(0, [GAUGE_COLORS.RED, GAUGE_COLORS.YELLOW, GAUGE_COLORS.GREEN])
      this.riskChartOption = setGuageChart(0, [GAUGE_COLORS.GREEN, GAUGE_COLORS.YELLOW, GAUGE_COLORS.RED])
    }
    if (checklistId) {
      let checklistItem = this.checklists.find(item => item._id == checklistId)
      if (checklistItem) {
        checklistItem.loading = true //item will be replaced with updated data, so no need to make it false
        checklistItem.status = undefined
        this.updateComplianceRate(this.checklists)
      }
    }
    else
      this.loading = true
    this.contractChecklistService.extractChecklistItemsFromContract(this.contractId, refresh, checklistId).subscribe({
      next: res => {
        if (res.success) {
          this.loading = false
          const checklistsInSync = res.data
          if (!checklistId) {//needless to call for every item refresh.
            this.aiLoading = !checklistsInSync
            this.getContractChecklistMappings();
          }
        }
      },
      error: err => {
        this.loading = false
        this.toastService.error('Error while extracting checklists.')
      }
    })
  }

  getContractChecklistMappings() {
    this.loading = true
    this.contractChecklistService.getContractChecklistMappings(this.contractId).subscribe(
      {
        next: res => {
          if (res.success && res.data) {
            this.checklists = res.data.checklists
            this.aiLoading = res.data.inProgressJob
            this.updateInSync(this.checklists)
            this.updateComplianceRate(this.checklists)
            this.getRiskScore()
          }
          this.loading = false
        },
        error: err => {
          this.loading = false
        }
      })
  }

  getRiskScore() {
    this.contractService.getContractRiskScore(this.contractId).subscribe(res => {
      if (res.success) {
        this.riskScore = res.data
        if (this.checklists && this.checklists.length)
          this.riskChartOption = setGuageChart(this.riskScore, [GAUGE_COLORS.GREEN, GAUGE_COLORS.YELLOW, GAUGE_COLORS.RED])
      }
    })
  }

  updateInSync(checklists: IGetContractChecklist[] = []) {
    this.checklistItemsInSync = checklists.every(item => item.checklistItemInSync ?? false)
  }

  updateChecklistsData(checklists: IGetContractChecklist[], lastElement?: boolean, singleElement?: boolean) {
    const updatedChecklistsMap = new Map(checklists.map(item => [item._id, item]));
    this.checklists = this.checklists.map(item => updatedChecklistsMap.get(item._id) ?? item).filter(item => item !== undefined)
    this.updateInSync(this.checklists)
    this.updateComplianceRate(this.checklists)
    if (lastElement) {
      if (!singleElement) this.aiLoading = false;// do not update for single element update.
      this.getRiskScore()
    }
  }

  updateComplianceRate(checklists: IGetContractChecklist[] = []) {
    let totalMappingsCount = 0
    this.statusCount = {
      [CONTRACT_CHECKLIST_STATUS.COMPLIANCE]: 0,
      [CONTRACT_CHECKLIST_STATUS.NOT_IN_COMPLIANCE]: 0,
      [CONTRACT_CHECKLIST_STATUS.SAVE_FOR_LATER]: 0
    }
    for (const checklist of checklists) {
      if (checklist.status) {
        this.statusCount[checklist.status]++;
        totalMappingsCount++;
      }
    }
    this.complianceRate = totalMappingsCount ? toFixedNumber((this.statusCount[CONTRACT_CHECKLIST_STATUS.COMPLIANCE] / totalMappingsCount) * 100, 2) : 0
    this.chartOption = setGuageChart(this.complianceRate, [GAUGE_COLORS.RED, GAUGE_COLORS.YELLOW, GAUGE_COLORS.GREEN])
  }

  getChecklistRules() {
    this.checklistService.getRulesLinkedToChecklist(this.selectedChecklist).subscribe((res) => {
      if (res && res.success) {
        this.rulesDialog.rules = res.data.impactRules;
        const currentRule = this.rulesDialog.rules.find(rule =>
          rule.rule === this.rulesDialog.selectedRule?.rule &&
          rule.impact === this.rulesDialog.selectedRule?.impact
        );
        if (currentRule) {
          this.rulesDialog.selectedRule = currentRule;
        }
        this.riskMatrixDialog.selectedImpact = this.riskMatrixDialog.selectedImpact ? this.riskMatrixDialog.selectedImpact : res.data.impact;
        this.riskMatrixDialog.selectedProbability = res.data.probability;
      } else {
        this.toastService.error('Error fetching the rules');
      }
    });
  }

  changeRule() {
    const data = { contract: this.contractId, checklistItem: this.selectedChecklist, impactRule: this.rulesDialog.selectedRule };
    this.contractChecklistService.updateContractChecklistMapping(data).subscribe(res => {
      if (res && res.success) {
        this.toastService.success('Successfully updated the impact rule for the selected checklist.');
        const checklistItem = this.checklists.find(item => item._id == this.selectedChecklist);
        if (checklistItem) checklistItem.impactRule = data.impactRule;
      } else {
        this.toastService.error('Error updating the impact rule for the selected checklist.');
      }
      this.closeRulesDialog();
    })
  }

  closeRulesDialog = _.debounce(() => {
    this.rulesDialog.visible = false;
    // this.getContractChecklistMappings();
  }, 500)

  mappingChanged(checklist: IGetContractChecklist, status: CONTRACT_CHECKLIST_STATUS) {
    if (this.role !== CONTRACT_USER_ROLE.OWNER && this.role !== CONTRACT_USER_ROLE.EDITOR) {
      this.toastService.error('You are not authorized to change the status of the checklist.');
      return;
    }
    this.contractChecklistService.saveContractChecklistMapping({ contract: this.contractId, checklistItem: checklist._id, status }).subscribe(res => {
      if (res.success) {
        // this.getContractChecklistMappings()
        const checklistItem = this.checklists.find(item => item._id == checklist._id);
        if (checklistItem) {
          checklistItem.status = status;
          checklistItem.aiGenerated = false;
        }
      }
    })
  }

  openChangeRuleDialog(data: { checklist: IChecklistItem['_id'], impactRule?: { rule: string, impact: number } }) {
    if (this.role !== CONTRACT_USER_ROLE.OWNER && this.role !== CONTRACT_USER_ROLE.EDITOR) {
      this.toastService.error('You are not authorized to change the rule of the checklist.');
      return;
    }
    this.selectedChecklist = data.checklist;
    this.rulesDialog.visible = true;
    this.rulesDialog.selectedRule = data.impactRule || { rule: '', impact: 0 };
    this.getChecklistRules();
  }

  openSourceDialog(checklist: { selectedChecklist: {}, currentIdx: number, source: string, sourceDocument: string, recommended?: string }) {
    this.sourceDialog.selectedChecklist = checklist.selectedChecklist;
    this.sourceDialog.currentIdx = checklist.currentIdx;
    this.sourceDialog.selectedSource = checklist.source;
    this.sourceDialog.selectedRecommended = checklist.recommended || '';
    this.sourceDialog.selectedSourceDocument = checklist.sourceDocument || '';
    this.sourceDialog.visible = true;
  }

  openRiskMatrixDialog(data: { checklist: IChecklistItem['_id'], impactRule?: { rule: string, impact: number } }) {
    this.selectedChecklist = data.checklist;
    this.riskMatrixDialog.visible = true;
    this.riskMatrixDialog.selectedImpact = data.impactRule?.impact || 0;
    this.getChecklistRules();
  }

  copyToClipboard() {
    const text = this.sourceDialog.selectedRecommended;
    navigator.clipboard.writeText(text);
    this.toastService.success('Copied to clipboard');
  }

  getDiffHtml(): SafeHtml {
    if (!this.sourceDialog.selectedSource || !this.sourceDialog.selectedRecommended) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const dmp = new (diff_match_patch as any)();
    const diffs = dmp.diff_main(this.sourceDialog.selectedSource, this.sourceDialog.selectedRecommended);
    dmp.diff_cleanupSemantic(diffs);

    let html = '';
    for (const diff of diffs) {
      const operation = diff[0];
      const text = diff[1];
      const escapedText = this.escapeHtml(text);
      if (operation === DIFF_DELETE) {
        html += `<span class="diff-deleted">${escapedText}</span>`;
      } else if (operation === DIFF_INSERT) {
        html += `<span class="diff-added">${escapedText}</span>`;
      } else {
        html += escapedText;
      }
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
  }
}
